/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import { Box, Chip, Paper, Skeleton } from '@mui/material';
import moment from 'moment';
import { Bar } from 'react-chartjs-2';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Download, ShareButton } from 'components/Download';

/**
 * 📊 Muestra el consumo acumulado diario en un gráfico de barras, comparándolo con el mes actual y el anterior.
 *
 * @param {object} props - Las propiedades del componente.
 * @param {object} props.totalProm - Consumo promedio del mes actual.
 * @param {object} props.totalPromLast - Consumo promedio del mes anterior.
 * @param {object} props.totalPromTotals - Consumo total acumulado.
 * @param {number} props.year - El año a mostrar.
 * @param {number} props.month - El mes a mostrar.
 * @returns {JSX.Element} Un componente que muestra el consumo acumulado.
 */
export default function ConsumeAccumulate({
  totalProm,
  totalPromLast,
  totalPromTotals,
  year,
  month,
}) {
  const intl = useIntl();
  const [days, setDays] = useState([]);
  const [filledData, setFilledData] = useState([]);
  const [totalPromData, setTotalPromData] = useState([]);
  const [totalPromLastData, setTotalPromLastData] = useState([]);
  const localeNav = useSelector((state) => state.prefs.locale);
  const chartRef = useRef();

  useEffect(() => {
    // 📅 Calcula los días del mes
    const daysInMonth = year
      ? moment(`${year}${month}`).daysInMonth()
      : moment().daysInMonth();
    const currentMonth = moment().month();
    const currentYear = moment().year();
    const daysArray = [];
    const newFilledData = Array(daysInMonth).fill(0);
    const newTotalPromData = Array(daysInMonth).fill(0);
    const newTotalPromLastData = Array(daysInMonth).fill(0);

    // 🔄 Inicializa los datos para cada día del mes
    for (let i = 1; i <= daysInMonth; i++) {
      const date = moment({
        year: year || currentYear,
        month: month ? month - 1 : currentMonth,
        day: i,
      });
      daysArray.push({
        day: i,
        weekday: date.format('ddd'),
        value: 0,
      });
    }

    // 🔢 Rellena los datos con la información proporcionada
    if (totalPromTotals) {
      Object.keys(totalPromTotals).forEach((key) => {
        const dayIndex = parseInt(key, 10) - 1;
        if (dayIndex < daysInMonth) {
          newFilledData[dayIndex] = totalPromTotals[key];
        }
      });
    }

    if (totalProm) {
      Object.keys(totalProm).forEach((key) => {
        const dayIndex = parseInt(key, 10) - 1;
        if (dayIndex < daysInMonth) {
          newTotalPromData[dayIndex] = totalProm[key];
        }
      });
    }

    if (totalPromLast) {
      Object.keys(totalPromLast).forEach((key) => {
        const dayIndex = parseInt(key, 10) - 1;
        if (dayIndex < daysInMonth) {
          newTotalPromLastData[dayIndex] = totalPromLast[key];
        }
      });
    }

    // 📥 Actualiza el estado con los datos calculados
    setFilledData(newFilledData);
    setTotalPromData(newTotalPromData);
    setTotalPromLastData(newTotalPromLastData);
    setDays(daysArray);
  }, [localeNav, totalPromTotals, totalProm, totalPromLast, month, year]);

  // 🏷️ Etiquetas para el eje X del gráfico
  const labels = days.map(
    (day) =>
      `${day.weekday.charAt(0).toUpperCase() + day.weekday.slice(1, 3)} ${day.day}`
  );

  // 📊 Datos para el gráfico
  const data = {
    labels: labels,
    datasets: [
      {
        // 🔋 Consumo total acumulado
        label: `${intl.formatMessage({ id: 'app.charge.board.dailyAcumConsump.dataSet.tooltip.title' })} kWh`,
        backgroundColor: '#00AFEF',
        data: filledData,
        type: 'bar',
      },
      {
        // 📏 Consumo promedio del mes actual
        label: `${intl.formatMessage({ id: 'app.charge.board.dailyAcumConsump.dataSet.tooltip.currentMonth' })} kWh`,
        borderColor: '#A53692',
        backgroundColor: '#A53692',
        borderWidth: 3,
        fill: false,
        data: totalPromData,
        type: 'line',
      },
      {
        // 📉 Consumo promedio del mes anterior
        label: `${intl.formatMessage({ id: 'app.charge.board.dailyAcumConsump.dataSet.tooltip.lastMonth' })} kWh`,
        borderColor: '#ED685E',
        backgroundColor: '#ED685E',
        fill: false,
        borderDash: [10, 5],
        borderWidth: 1,
        data: totalPromLastData,
        type: 'line',
      },
    ],
  };

  // ⚙️ Opciones de configuración del gráfico
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        onClick: (e, legendItem, legend) => {
          const chart = legend.chart;
          const index = legendItem.datasetIndex;
          const meta = chart.getDatasetMeta(index);

          // Toggle visibility
          meta.hidden =
            meta.hidden === null ? !chart.data.datasets[index].hidden : null;

          // Re-render chart
          chart.update();
        },
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        borderWidth: 1,
        callbacks: {
          label: function (tooltipItem) {
            const dataset = tooltipItem.dataset;
            const value = tooltipItem.raw.toLocaleString('en', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
            return `${dataset.label}: ${value}`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
      },
      y: {
        position: 'right',
        ticks: {
          callback: function (value) {
            return value.toFixed(0);
          },
          beginAtZero: true,
          autoSkip: true,
        },
      },
    },
    animation: {
      onComplete: (animation) => {
        const chart = animation.chart;
        const ctx = chart.ctx;
        ctx.textAlign = 'center';
        ctx.fillStyle = 'rgba(0, 0, 0, 1)';
        ctx.textBaseline = 'bottom';

        chart.data.datasets[0].data.forEach((data, index) => {
          const meta = chart.getDatasetMeta(0);
          const element = meta.data[index];

          if (element) {
            const posX = element.x;
            const posY = element.y;

            ctx.fillText(data.toFixed(0), posX, posY - 5);
          }
        });
      },
    },
  };

  return (
    <Paper
      sx={{ width: '100%', p: 2, overflowX: 'hidden', overflowY: 'auto' }}
      ref={chartRef}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        {/* 🏷️ Título del componente */}
        <Chip
          label={intl.formatMessage({
            id: 'app.chargeSolar.board.generationDaily.title',
          })}
          sx={{
            fontWeight: 700,
            backgroundColor: '#730065',
            fontSize: '16px',
            color: 'white',
          }}
        />
        <Box display="flex" alignItems="center" gap={1}>
          <Download
            itemRef={chartRef}
            name={intl.formatMessage({
              id: 'app.chargeSolar.board.generationDaily.title',
            })}
          />
          <ShareButton
            itemRef={chartRef}
            name={intl.formatMessage({
              id: 'app.chargeSolar.board.generationDaily.title',
            })}
          />
        </Box>
      </Box>
      {/* 📊 Contenedor del gráfico */}
      <Box height={400}>
        {/* 📉 Gráfico de barras si hay datos, esqueleto si no */}
        {totalProm ? (
          <Bar data={data} options={options} width="100%" height={400} />
        ) : (
          <Skeleton variant="rectangular" width="100%" height={400} />
        )}
      </Box>
    </Paper>
  );
}
